import React, { useEffect, useRef, useState } from 'react';
import { Chip, Typography, Button, CircularProgress, Grid, Box, IconButton } from '@mui/material';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { ReportActions } from '../../../../../Services/_actions/Report/report.action.js';
import { useSnackBar } from '../../../../FlowBuilder/Common/SnackBar/index.tsx';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { campaignLeadManagementAtom } from '../../../../../Services/_states/Leads/index.ts';
import { userInformationAtom } from '../../../../../Services/_states/auth.js';
import { LeadFormActions } from '../../../../../Services/_actions/index.js';
import HtmlPreviewWrapper from './HtmlPreviewWrapper.tsx';
import CampaignFormSelectionModal from '../../../../LeadWebForm/CampaignFormSelectionModal.tsx';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { reportTemplateDetailsAtom } from '../../../../../Services/_states/Report/index.ts';
import { useNavigate } from 'react-router';
import AddIcon from '@mui/icons-material/Add';
import DateRangeFilter from '../../../../Shared/DateRangeFilter/DateRangeFilter.tsx';

type TemplateItem = {
  _id: string;
  name: string;
  description?: string;
  created_at?: string;
  is_published?: boolean;
  charts_count?: number;
};

const a11yProps = (index: number) => {
  return {
    id: `report-tab-${index}`,
    'aria-controls': `report-tabpanel-${index}`,
  };
};

const TabPanel: React.FC<{ children?: React.ReactNode; index: number; value: number }> = ({
  children,
  value,
  index,
}) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`report-tabpanel-${index}`}
      aria-labelledby={`report-tab-${index}`}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
};

const ViewReport: React.FC<any> = () => {
  const actionsRef = useRef(ReportActions());
  const reportActions = actionsRef.current;
  const action = LeadFormActions();
  const { showSnackBar } = useSnackBar();
  const navigate = useNavigate();
  const setTemplateReport = useSetRecoilState(reportTemplateDetailsAtom);
  const [campaignState, setCampaignState] = useRecoilState(campaignLeadManagementAtom);
  const [campaignFormModalProps, setCampaignFormModalProps] = useState({
    open: false,
    isEdit: false,
    formData: null,
  });
  const modalData = campaignFormModalProps?.formData;
  const selectedForm =
    campaignState?.selectedForm || modalData?.webForm || modalData?.campaign || null;
  const selectedFormId =
    selectedForm?.leads_form_id || selectedForm?.id || selectedForm?._id || null;
  const selectedFormName =
    selectedForm?.form_name || selectedForm?.leads_form_name || selectedForm?.name || null;
  const [templates, setTemplates] = useState<TemplateItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isPublish, setIsPublish] = useState(true);
  const [tabValue, setTabValue] = useState<number>(0);
  const [htmlPreview, setHtmlPreview] = useState<string>('');
  const [templateName, setTemplateName] = useState<string>('');
  const leadId = null;
  const [dateRange, setDateRange] = useState<{ startDate: Date | null; endDate: Date | null }>({
    startDate: null,
    endDate: null,
  });

  const buildFilters = () => {
    if (!dateRange.startDate || !dateRange.endDate) return {};
    return {
      global_filters: {
        start_date: new Date(dateRange.startDate).toISOString().split('T')[0] + 'T00:00:00',
        end_date: new Date(dateRange.endDate).toISOString().split('T')[0] + 'T23:59:59',
      },
    };
  };

  const handleViewReport = async (tOrId: TemplateItem | string) => {
    const id = typeof tOrId === 'string' ? tOrId : tOrId?._id;
    if (!id) {
      showSnackBar('No template id provided', 'error');
      return;
    }
    try {
      const res = await reportActions.getHtmlPreview(id, {
        ...buildFilters(),
        parameters: {},
        format: 'html',
      });
      if (!res || !res.data) {
        showSnackBar('Failed to generate report preview', 'error');
        return;
      }
      setHtmlPreview(res.data);
      setTemplateName(template?.name ?? '');
      showSnackBar('Report Preview generated successfully', 'success');
    } catch (e) {
      console.error('handleViewReport error', e);
      // showSnackBar('Failed to Html Preview', 'error');
    }
  };

  useEffect(() => {
    getReportTemplates();
  }, [selectedFormId, dateRange]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    const template = templates?.[newValue];
    if (template) {
      handleViewReport(template); // fetch HTML for selected tab
    }
  };

  useEffect(() => {
    if (!loading && Array.isArray(templates) && templates.length > 0) {
      setTabValue(0);
      handleViewReport(templates[0]);
    }
  }, [loading, templates]);

  const getReportTemplates = async () => {
    if (!selectedFormId) {
      setTemplates([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const res = await reportActions.getReportTemplates(selectedFormId, isPublish);
      if (res?.status === 200 && Array.isArray(res?.data)) {
        setTemplates([...res?.data]);
        setTabValue(0);
      } else {
        setTemplates([]);
        showSnackBar('Failed to fetch report templates', 'error');
      }
    } catch (e) {
      setTemplates([]);
      showSnackBar('Failed to fetch report templates', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getReportTemplates();
  }, [isPublish, selectedFormId]);

  const handleSubmitCampaignForm = (data) => {
    try {
      const leadFormsKey = 'leadWebFormData';
      const leadWebFormData = JSON.parse(localStorage.getItem(leadFormsKey) || 'null') || {};

      if (data && Object.keys(data).length > 0) {
        localStorage.setItem(leadFormsKey, JSON.stringify(data));
        setCampaignFormModalProps((prev) => ({
          ...prev,
          open: false,
          isEdit: false,
          formData: data,
        }));
      } else if (leadWebFormData?.campaign?.name && leadWebFormData?.webForm?.leads_form_name) {
        setCampaignFormModalProps((prev) => ({
          ...prev,
          open: false,
          isEdit: false,
          formData: leadWebFormData,
        }));
      } else {
        // no data provided and nothing in localStorage -> just close modal
        setCampaignFormModalProps((prev) => ({ ...prev, open: false }));
      }
    } catch (error) {
      console.warn(error, 'error');
    }
  };

  useEffect(() => {
    try {
      const leadFormsKey = 'leadWebFormData';
      const leadWebFormData = JSON.parse(localStorage.getItem(leadFormsKey) || 'null') || {};
      if (
        !leadWebFormData ||
        Object.keys(leadWebFormData).length <= 1 ||
        (leadWebFormData?.campaign === null && leadWebFormData?.webForm === null)
      ) {
        // open modal to capture campaign & form
        setCampaignFormModalProps((prev) => ({ ...prev, open: true, isEdit: false }));
      } else {
        setCampaignFormModalProps((prev) => ({
          ...prev,
          open: false,
          formData: leadWebFormData,
          isEdit: false,
        }));
      }
    } catch (error) {
      console.warn(error, 'error');
      setCampaignFormModalProps((prev) => ({ ...prev, open: true, isEdit: false }));
    }
  }, []);

  const toCreateFormat = () => {
    setTemplateReport({ leads_form_id: selectedFormId ?? null } as any);
    navigate('/r/records/report-list', {
      state: {
        leads_form_id: selectedFormId ?? null,
        leads_form_name: selectedFormName || null,
      },
    });
  };

  const handleDownloadReport = async () => {
    try {
      if (!templates?.[tabValue]?._id) {
        showSnackBar('No report template selected', 'error');
        return;
      }

      const id = templates[tabValue]._id;

      const payload = {
        ...buildFilters(),
        parameters: {},
        format: 'excel',
      };

      const res = await reportActions.getHtmlPreview(id, payload);

      if (res?.status === 200) {
        const blob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${templates[tabValue].name || 'report'}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        showSnackBar('Report downloaded successfully', 'success');
      } else {
        showSnackBar('Failed to download report', 'error');
      }
    } catch (e) {
      console.error('Download error', e);
      showSnackBar('Error while downloading report', 'error');
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent:
            campaignFormModalProps?.formData?.campaign?.name &&
            campaignFormModalProps?.formData?.webForm?.leads_form_name
              ? 'space-between'
              : 'flex-end',
        }}
      >
        {campaignFormModalProps?.formData?.campaign?.name &&
          campaignFormModalProps?.formData?.webForm?.leads_form_name && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '30px',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                backgroundColor: '#f9f9f9',
                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                mb: 0.5,
              }}
            >
              <IconButton
                onClick={() => {
                  navigate(-1);
                }}
                color="primary"
                size="medium"
                sx={{
                  marginRight: 2,
                  backgroundColor: '#e3f2fd',
                  '&:hover': { backgroundColor: '#bbdefb' },
                }}
              >
                <ArrowBackIcon />
              </IconButton>

              <Typography
                component="span"
                variant="h5"
                sx={{ fontWeight: 'bold', color: '#333', fontSize: '14px' }}
              >
                {`${campaignFormModalProps?.formData?.campaign?.name} (${campaignFormModalProps?.formData?.webForm?.leads_form_name})`}
              </Typography>
              <Box>
                {!leadId && (
                  <IconButton
                    onClick={() => {
                      setCampaignFormModalProps((prev) => ({ ...prev, open: true, isEdit: true }));
                    }}
                    color="primary"
                    size="medium"
                    sx={{
                      backgroundColor: '#e3f2fd',
                      '&:hover': { backgroundColor: '#bbdefb' },
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                )}
              </Box>
            </Box>
          )}

        <Box>
          <Button
            variant="contained"
            size="small"
            onClick={toCreateFormat}
            sx={{ width: { xs: '100%', sm: 'auto', padding: '8px' } }}
          >
            Report List
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          padding: '5px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Grid container spacing={2} sx={{ alignItems: 'center' }}>
          <Grid item xs={6}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: '500' }}>
              View Reports
            </Typography>
          </Grid>
          {/* <Grid item xs={6} textAlign="right">
            <Button
              variant="contained"
              size="small"
              onClick={toCreateFormat}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
           Report List
            </Button>
          </Grid> */}
        </Grid>

        <Box sx={{ mt: 2 }}>
          <Box sx={{ mb: 2, float: 'right', display: 'flex', gap: 1 }}>
            <DateRangeFilter
              onApply={(range) => {
                if (!range.startDate && !range.endDate) {
                  setDateRange({ startDate: null, endDate: null });
                } else {
                  setDateRange(range);
                }
              }}
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleDownloadReport}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Download Report
            </Button>
          </Box>
          {/* Loading */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                {templates.length > 0 ? (
                  <Tabs
                    value={tabValue}
                    onChange={handleChange}
                    aria-label="report templates tabs"
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    {templates.map((t, idx) => (
                      <Tab
                        key={t._id ?? idx}
                        label={t.name ?? `Report ${idx + 1}`}
                        {...a11yProps(idx)}
                      />
                    ))}
                  </Tabs>
                ) : (
                  <Typography sx={{ p: 2 }}>No report templates available.</Typography>
                )}
              </Box>
              {templates.map((t, idx) => (
                <TabPanel key={t._id ?? idx} value={tabValue} index={idx}>
                  <HtmlPreviewWrapper htmlContent={htmlPreview} templateName={templateName} />
                </TabPanel>
              ))}
            </>
          )}
        </Box>
        {campaignFormModalProps?.open && (
          <CampaignFormSelectionModal
            open={campaignFormModalProps?.open}
            onClose={handleSubmitCampaignForm}
            isEdit={campaignFormModalProps?.isEdit || false}
            selectedCampaign={campaignFormModalProps?.formData?.campaign || null}
            selectedForm={campaignFormModalProps?.formData?.webForm || null}
          />
        )}
      </Box>
    </>
  );
};

export default ViewReport;
